/**
 * @fileoverview Recommended searches list — pending practitioner approvals.
 *
 * Pure presentational + action component: the manual-trigger form is now
 * mounted at the page level (`_layout.jobs.$jobId.searches.tsx`) so that
 * "Configure" buttons here push a `ManualTriggerPrefill` upward via
 * `onConfigure` rather than mounting their own copy of the form.
 *
 * Each recommendation row exposes its `inputPayload` as editable inputs so
 * the practitioner can tweak values (e.g. correct an ACN) before approving.
 * The edited payload is sent through to `approveSearchRecommendation` and
 * persisted back onto the recommendation row for the audit trail.
 */

import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useConvexMutation, convexQuery } from "@convex-dev/react-query";
import { Info } from "lucide-react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/primitives/card";
import { Badge } from "@/ui/primitives/badge";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Checkbox } from "@/ui/primitives/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/ui/composed/overlays/hover-card";
import { toast } from "sonner";
import type { ManualTriggerPrefill } from "./-ui.searches-manual-trigger";

/**
 * Products that need a multi-step lookup before a real order can be
 * submitted (e.g. ASIC personal extract requires a name lookup to obtain
 * `parentOrderId` + the matching `nameIdentifiers`). Recommendations for
 * these products are surfaced with a "Configure" button that pre-populates
 * the manual-trigger form rather than the standard one-click "Approve".
 */
const REQUIRES_CONFIGURE_BEFORE_APPROVAL = new Set<string>([
  "asic_personal_extract_current_historical",
]);

/**
 * Friendlier labels for recommendation payload keys. Anything not in the
 * map falls back to a humanised form of the raw key.
 */
const PAYLOAD_LABELS: Record<string, string> = {
  organisationNumber: "ACN / ABN",
  organisationNumberType: "Number type",
  informationType: "Information type",
  familyName: "Family name",
  givenNames: "Given names",
  extractType: "Extract type",
  state: "State",
  streetName: "Street name",
  streetType: "Street type",
  streetNumberFrom: "Street number",
  postCode: "Postcode",
  suburb: "Suburb",
  titleReference: "Title reference",
  companyName: "Company name",
};

function humaniseKey(key: string): string {
  return PAYLOAD_LABELS[key] ?? key.replace(/([A-Z])/g, " $1").toLowerCase();
}

type RecommendationReason =
  | "determinative"
  | "director_extract"
  | "holding_co"
  | "related_entity"
  | "director_residence_land"
  | "company_ppb_land"
  | "company_additional_pob_land"
  | "change_detected"
  | "manual";

type RecommendationGeneratedBy =
  | "system"
  | "agent_corporate_summary"
  | "agent_related_party"
  | "agent_security_interests"
  | "user";

/**
 * Title-cased label + plain-English description for each reason the
 * recommendations engine can stamp on a row. Drives the badge label and
 * the "Why this was suggested" tooltip.
 */
const REASON_META: Record<
  RecommendationReason,
  { label: string; description: string }
> = {
  determinative: {
    label: "Determinative",
    description:
      "Baseline search every job runs. Seeded automatically when the job is created.",
  },
  director_extract: {
    label: "Director extract",
    description:
      "An officer or director was identified — running their personal extract uncovers other directorships and history.",
  },
  holding_co: {
    label: "Holding company",
    description:
      "A related company (shareholder, subsidiary, or other corporate entity) was identified, so we suggest extracting it too.",
  },
  related_entity: {
    label: "Related entity",
    description:
      "A controlled entity was identified in the related parties — running an extract may reveal additional exposure or connections.",
  },
  director_residence_land: {
    label: "Director's residence",
    description:
      "A director's residential address was identified — a land title search may surface real-property assets.",
  },
  company_ppb_land: {
    label: "Principal place of business",
    description:
      "Seeded from the company's principal place of business address — a land title search may surface occupied real property.",
  },
  company_additional_pob_land: {
    label: "Additional place of business",
    description:
      "Seeded from a trading address added manually in job settings — a land title search may surface occupied real property the company search missed.",
  },
  change_detected: {
    label: "Change detected",
    description:
      "Registry data has changed since the previous order — a refreshed search is suggested.",
  },
  manual: {
    label: "Manually added",
    description: "Added by a practitioner via the manual trigger.",
  },
};

const FALLBACK_REASON_META = {
  label: "Other",
  description: "No description available for this recommendation reason.",
} as const;

/**
 * Where the recommendation came from — surfaced as the "Source" line in the
 * tooltip so practitioners can trace each suggestion back to the agent that
 * raised it.
 */
const SOURCE_META: Record<
  RecommendationGeneratedBy,
  { label: string; description: string }
> = {
  system: {
    label: "Morgan (auto-seeded)",
    description:
      "Seeded automatically from the job's intake details when it was created.",
  },
  agent_corporate_summary: {
    label: "Corporate Summary agent",
    description:
      "Surfaced by the Corporate Summary agent after reviewing the documents indexed for this job.",
  },
  agent_related_party: {
    label: "Related Parties agent",
    description:
      "Surfaced by the Related Parties agent after reviewing the documents indexed for this job.",
  },
  agent_security_interests: {
    label: "Security Interests agent",
    description:
      "Surfaced by the Security Interests agent after reviewing the documents indexed for this job.",
  },
  user: {
    label: "Practitioner",
    description: "Added manually by a practitioner on this job.",
  },
};

const FALLBACK_SOURCE_META = {
  label: "Unknown source",
  description: "We couldn't determine where this recommendation came from.",
} as const;

type RecommendationRow = {
  _id: Id<"infoTrackRecommendations">;
  productKey: string;
  status: string;
  reason: RecommendationReason;
  generatedBy: RecommendationGeneratedBy;
  inputPayload: Record<string, unknown>;
  displayName: string;
  sourceEntityLabel?: string;
  wired: boolean;
};

interface SearchesRecommendedListProps {
  jobId: Id<"jobs">;
  rows: RecommendationRow[];
  /**
   * Controls whether the "all candidates" view is rendered (snoozed,
   * rejected, and not-yet-suggested products). Owned by the page so the
   * toggle can sit in the section header next to "Recommended".
   */
  showAllCandidates: boolean;
  /**
   * Pushes a prefill payload up to the page-level manual trigger. Called
   * when "Configure" is clicked on a recommendation that requires a
   * synchronous lookup before a real order can be submitted.
   */
  onConfigure: (prefill: ManualTriggerPrefill) => void;
}

export function SearchesRecommendedList({
  jobId,
  rows,
  showAllCandidates,
  onConfigure,
}: SearchesRecommendedListProps) {
  const pending = rows
    .filter((r) => r.status === "pending" && r.wired)
    .sort((a, b) => {
      const reasonOrder = (r: RecommendationRow) =>
        r.reason === "determinative" ? 0 : 1;
      const reasonDiff = reasonOrder(a) - reasonOrder(b);
      if (reasonDiff !== 0) return reasonDiff;

      const productOrder = (r: RecommendationRow) => {
        if (r.productKey.startsWith("asic_")) return 0;
        if (r.productKey.startsWith("ppsr_")) return 1;
        return 2;
      };
      return productOrder(a) - productOrder(b);
    });
  const unwiredPending = rows.filter((r) => r.status === "pending" && !r.wired);
  const snoozedRows = rows.filter((r) => r.status === "snoozed" && r.wired);
  const rejectedRows = rows.filter((r) => r.status === "rejected" && r.wired);
  const supersededRows = rows.filter(
    (r) => r.status === "superseded" && r.wired,
  );
  const determinativePending = pending.filter(
    (r) => r.reason === "determinative",
  );

  // Coverage data drives the "Not yet suggested" section in the all-candidates
  // view. We only fetch it when the practitioner opens that view to keep the
  // default render path lean.
  const { data: coverageRows } = useQuery({
    ...convexQuery(api.jobs.searches.queries.listSearchCoverageForJob, {
      jobId,
    }),
    enabled: showAllCandidates,
  });

  const approveOne = useConvexMutation(
    api.jobs.searches.mutations.approveSearchRecommendation,
  );
  const approveAll = useConvexMutation(
    api.jobs.searches.mutations.approveAllDeterminativeRecommendations,
  );
  const rejectOne = useConvexMutation(
    api.jobs.searches.mutations.rejectSearchRecommendation,
  );
  const snoozeOne = useConvexMutation(
    api.jobs.searches.mutations.snoozeSearchRecommendation,
  );

  const approveAllMutation = useMutation({
    mutationFn: () => approveAll({ jobId }),
    onSuccess: (data) => {
      toast.success(
        data.approvedCount === 0
          ? "No determinative searches were pending."
          : `Approved ${data.approvedCount} determinative search${data.approvedCount === 1 ? "" : "es"}.`,
      );
    },
    onError: (e: Error) =>
      toast.error(e.message || "Could not approve searches."),
  });

  const [confirmRec, setConfirmRec] = useState<RecommendationRow | null>(null);

  /**
   * Per-recommendation edit buffer — initialised lazily from each row's
   * stored payload on first render. Edits stay local until "Approve" is
   * clicked, at which point the full edited payload is sent through.
   */
  const [edits, setEdits] = useState<Record<string, Record<string, unknown>>>(
    {},
  );

  const getEditedPayload = useCallback(
    (rec: RecommendationRow): Record<string, unknown> =>
      edits[rec._id] ?? rec.inputPayload,
    [edits],
  );

  const setFieldValue = useCallback(
    (rec: RecommendationRow, key: string, value: unknown) => {
      setEdits((prev) => {
        const current = prev[rec._id] ?? rec.inputPayload;
        return {
          ...prev,
          [rec._id]: { ...current, [key]: value },
        };
      });
    },
    [],
  );

  const configurePersonalExtract = useCallback(
    (rec: RecommendationRow) => {
      const payload = getEditedPayload(rec);
      const familyName = String(payload.familyName ?? "").trim();
      const givenNames = String(payload.givenNames ?? "").trim();
      const extractTypeRaw = String(payload.extractType ?? "Historical");
      const extractType: "Current" | "Historical" =
        extractTypeRaw === "Current" ? "Current" : "Historical";
      onConfigure({
        nonce: Date.now(),
        productKey: rec.productKey,
        recommendationId: rec._id,
        asicPersonalExtract: {
          familyName,
          givenNames,
          extractType,
        },
      });
    },
    [onConfigure, getEditedPayload],
  );

  return (
    <div className="space-y-4">
      {unwiredPending.length > 0 ? (
        <Card className="border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
          <CardHeader>
            <CardTitle className="text-base">
              {unwiredPending.length} recommendation
              {unwiredPending.length === 1 ? "" : "s"} pending API wiring
            </CardTitle>
            <CardDescription>
              {unwiredPending
                .map((r) => r.displayName)
                .filter((v, i, a) => a.indexOf(v) === i)
                .join(", ")}{" "}
              cannot be submitted yet — the InfoTrack endpoints for these
              products are not yet configured. Provide the API spec and they
              will appear here for approval.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      {determinativePending.length > 1 ? (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="secondary"
            disabled={approveAllMutation.isPending}
            onClick={() => approveAllMutation.mutate()}
          >
            Approve all determinative
          </Button>
        </div>
      ) : null}

      {pending.length === 0 ? (
        <div className="rounded-lg border bg-background p-6 text-sm text-muted-foreground">
          No pending recommendations. New suggestions appear here when agents
          flag searches.
        </div>
      ) : (
        <PendingRecommendationGroups
          pending={pending}
          getEditedPayload={getEditedPayload}
          setFieldValue={setFieldValue}
          configurePersonalExtract={configurePersonalExtract}
          setConfirmRec={setConfirmRec}
          snoozeOne={snoozeOne}
          rejectOne={rejectOne}
        />
      )}

      {showAllCandidates ? (
        <AllCandidatesView
          snoozedRows={snoozedRows}
          rejectedRows={rejectedRows}
          supersededRows={supersededRows}
          coverageRows={coverageRows ?? null}
          setConfirmRec={setConfirmRec}
        />
      ) : null}

      <Dialog
        open={!!confirmRec}
        onOpenChange={(o) => !o && setConfirmRec(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve search</DialogTitle>
          </DialogHeader>
          {confirmRec ? (
            <p className="text-sm text-muted-foreground">
              Submit{" "}
              <span className="font-medium text-foreground">
                {confirmRec.displayName}
              </span>{" "}
              to InfoTrack? Your firm is billed by InfoTrack for this search.
            </p>
          ) : null}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmRec(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={async () => {
                if (!confirmRec) return;
                try {
                  const editedPayload = edits[confirmRec._id];
                  await approveOne({
                    recommendationId: confirmRec._id,
                    ...(editedPayload ? { inputPayload: editedPayload } : {}),
                  });
                  toast.success("Search submitted");
                  setConfirmRec(null);
                } catch (e) {
                  toast.error(
                    e instanceof Error ? e.message : "Approval failed.",
                  );
                }
              }}
            >
              Approve and submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PendingRecommendationGroups — groups pending recommendations under headings
// (Company, Directors, Holding Companies) for easier scanning.
// ---------------------------------------------------------------------------

type RecommendationGroup = "company" | "directors" | "related_entities";

const GROUP_LABELS: Record<RecommendationGroup, string> = {
  company: "Company",
  directors: "Directors",
  related_entities: "Related Entities",
};

const GROUP_ORDER: RecommendationGroup[] = [
  "company",
  "directors",
  "related_entities",
];

function getRecommendationGroup(rec: RecommendationRow): RecommendationGroup {
  if (
    rec.reason === "determinative" ||
    rec.reason === "company_ppb_land" ||
    rec.reason === "company_additional_pob_land"
  ) {
    return "company";
  }
  if (
    rec.reason === "director_extract" ||
    rec.reason === "director_residence_land"
  ) {
    return "directors";
  }
  return "related_entities";
}

interface PendingRecommendationGroupsProps {
  pending: RecommendationRow[];
  getEditedPayload: (rec: RecommendationRow) => Record<string, unknown>;
  setFieldValue: (rec: RecommendationRow, key: string, value: unknown) => void;
  configurePersonalExtract: (rec: RecommendationRow) => void;
  setConfirmRec: (rec: RecommendationRow) => void;
  snoozeOne: (args: {
    recommendationId: Id<"infoTrackRecommendations">;
  }) => Promise<unknown>;
  rejectOne: (args: {
    recommendationId: Id<"infoTrackRecommendations">;
  }) => Promise<unknown>;
}

function PendingRecommendationGroups({
  pending,
  getEditedPayload,
  setFieldValue,
  configurePersonalExtract,
  setConfirmRec,
  snoozeOne,
  rejectOne,
}: PendingRecommendationGroupsProps) {
  const grouped = useMemo(() => {
    const map = new Map<RecommendationGroup, RecommendationRow[]>();
    for (const group of GROUP_ORDER) {
      map.set(group, []);
    }
    for (const rec of pending) {
      const group = getRecommendationGroup(rec);
      map.get(group)?.push(rec);
    }
    return map;
  }, [pending]);

  return (
    <div className="space-y-4">
      {GROUP_ORDER.map((group) => {
        const rows = grouped.get(group) ?? [];
        if (rows.length === 0) return null;
        return (
          <div key={group} className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">
              {GROUP_LABELS[group]}
            </h3>
            <ul className="divide-y rounded-lg border bg-background">
              {rows.map((rec) => (
                <li key={rec._id} className="space-y-3 p-4">
                  <div className="flex flex-row flex-wrap items-start justify-between gap-2">
                    <div className="space-y-1">
                      <div className="text-sm font-medium">
                        {rec.displayName}
                      </div>
                      {rec.sourceEntityLabel ? (
                        <div className="text-sm text-foreground/80">
                          {rec.sourceEntityLabel}
                        </div>
                      ) : null}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <ReasonBadge
                          reason={rec.reason}
                          generatedBy={rec.generatedBy}
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {REQUIRES_CONFIGURE_BEFORE_APPROVAL.has(
                        rec.productKey,
                      ) ? (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => configurePersonalExtract(rec)}
                        >
                          Configure
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => setConfirmRec(rec)}
                        >
                          Approve
                        </Button>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          snoozeOne({ recommendationId: rec._id })
                            .then(() => toast.success("Snoozed"))
                            .catch((e: Error) =>
                              toast.error(e.message || "Could not snooze."),
                            )
                        }
                      >
                        Snooze
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() =>
                          rejectOne({ recommendationId: rec._id })
                            .then(() => toast.success("Rejected"))
                            .catch((e: Error) =>
                              toast.error(e.message || "Could not reject."),
                            )
                        }
                      >
                        Reject
                      </Button>
                    </div>
                  </div>

                  <PayloadEditor
                    payload={getEditedPayload(rec)}
                    recommendationId={rec._id}
                    onChange={(key, value) => setFieldValue(rec, key, value)}
                  />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PayloadEditor — renders one labelled input per top-level payload key.
// Booleans become a Checkbox, numbers become a number input, everything
// else is treated as free text. Nested objects/arrays fall back to a
// read-only JSON dump (the recommendation engine only emits flat payloads,
// so this is a safety net rather than a real codepath).
// ---------------------------------------------------------------------------

interface PayloadEditorProps {
  payload: Record<string, unknown>;
  recommendationId: Id<"infoTrackRecommendations">;
  onChange: (key: string, value: unknown) => void;
}

function PayloadEditor({
  payload,
  recommendationId,
  onChange,
}: PayloadEditorProps) {
  const entries = useMemo(() => Object.entries(payload), [payload]);

  if (entries.length === 0) {
    return (
      <div className="text-xs text-muted-foreground">
        No payload — this product takes no input parameters.
      </div>
    );
  }

  return (
    <div className="grid gap-3 rounded-md border bg-muted/30 p-3 sm:grid-cols-2">
      {entries.map(([key, value]) => {
        const inputId = `rec-${recommendationId}-${key}`;
        const label = humaniseKey(key);

        if (typeof value === "boolean") {
          return (
            <div key={key} className="flex items-center gap-2 sm:col-span-2">
              <Checkbox
                id={inputId}
                checked={value}
                onCheckedChange={(v) => onChange(key, v === true)}
              />
              <Label htmlFor={inputId} className="text-xs capitalize">
                {label}
              </Label>
            </div>
          );
        }

        if (typeof value === "number") {
          return (
            <div key={key} className="space-y-1">
              <Label htmlFor={inputId} className="text-xs capitalize">
                {label}
              </Label>
              <Input
                id={inputId}
                type="number"
                value={value}
                onChange={(e) => {
                  const next = e.target.value;
                  onChange(
                    key,
                    next === ""
                      ? ""
                      : Number.isNaN(Number(next))
                        ? next
                        : Number(next),
                  );
                }}
              />
            </div>
          );
        }

        if (typeof value === "string" || value == null) {
          return (
            <div key={key} className="space-y-1">
              <Label htmlFor={inputId} className="text-xs capitalize">
                {label}
              </Label>
              <Input
                id={inputId}
                value={value == null ? "" : String(value)}
                onChange={(e) => onChange(key, e.target.value)}
              />
            </div>
          );
        }

        return (
          <div key={key} className="space-y-1 sm:col-span-2">
            <Label className="text-xs capitalize">{label}</Label>
            <pre className="overflow-auto rounded-md border bg-background p-2 font-mono text-xs">
              {JSON.stringify(value, null, 2)}
            </pre>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReasonBadge — title-cased label + hover tooltip explaining the reason and
// where the recommendation was sourced from. Replaces the old plain badge
// that just rendered `reason.replace(/_/g, " ")` (which produced lowercase
// strings like "company ppb land").
// ---------------------------------------------------------------------------

interface ReasonBadgeProps {
  reason: RecommendationReason;
  generatedBy: RecommendationGeneratedBy;
}

function ReasonBadge({ reason, generatedBy }: ReasonBadgeProps) {
  const reasonMeta = REASON_META[reason] ?? FALLBACK_REASON_META;
  const sourceMeta = SOURCE_META[generatedBy] ?? FALLBACK_SOURCE_META;

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      {/*
        Radix's `asChild` clones the trigger and tries to forward a ref onto
        it. Our `Badge` primitive is a plain function component and doesn't
        forward refs, which silently breaks Radix's positioning logic and the
        card never appears (you just see the `cursor-help` pointer). Wrapping
        in a native <span> gives Radix a proper element to attach to, while
        the Badge stays as the visual.
      */}
      <HoverCardTrigger asChild>
        <span
          tabIndex={0}
          className="inline-flex cursor-help rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`${reasonMeta.label} — hover for explanation`}
        >
          <Badge
            variant="outline"
            className="inline-flex items-center gap-1 font-normal"
          >
            {reasonMeta.label}
            <Info className="h-3 w-3 text-muted-foreground" aria-hidden />
          </Badge>
        </span>
      </HoverCardTrigger>
      <HoverCardContent side="top" className="w-80 space-y-3 text-sm">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Why this was suggested
          </p>
          <p className="font-medium leading-snug">{reasonMeta.label}</p>
          <p className="leading-relaxed text-muted-foreground">
            {reasonMeta.description}
          </p>
        </div>
        <div className="flex items-center gap-2 border-t pt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Source
          </p>
          <Badge variant="secondary" className="font-normal">
            {sourceMeta.label}
          </Badge>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

// ---------------------------------------------------------------------------
// AllCandidatesView — auditing view shown when "Show all candidates" is on.
// Renders three groups so practitioners can verify nothing fell through:
//   1. Snoozed recommendations (still actionable)
//   2. Rejected recommendations (deliberately declined)
//   3. Products with no records yet, with an explanation of why
// ---------------------------------------------------------------------------

type CoverageRow = {
  productKey: string;
  displayName: string;
  productLabel: string;
  registry: "asic" | "ppsr" | "lto";
  kind: "determinative" | "discretionary" | "non_infotrack";
  pendingCount: number;
  approvedCount: number;
  rejectedCount: number;
  snoozedCount: number;
  inProgressCount: number;
  completedCount: number;
  failedCount: number;
  reasonNotSuggested: string;
};

interface AllCandidatesViewProps {
  snoozedRows: RecommendationRow[];
  rejectedRows: RecommendationRow[];
  supersededRows: RecommendationRow[];
  coverageRows: CoverageRow[] | null;
  setConfirmRec: (rec: RecommendationRow) => void;
}

const REGISTRY_LABEL: Record<CoverageRow["registry"], string> = {
  asic: "ASIC",
  ppsr: "PPSR",
  lto: "Land titles",
};

function AllCandidatesView({
  snoozedRows,
  rejectedRows,
  supersededRows,
  coverageRows,
  setConfirmRec,
}: AllCandidatesViewProps) {
  // Products in the catalogue that have no rec/order records on this job.
  // These are the practitioner's "what wasn't suggested?" worklist.
  const notSuggested = useMemo(() => {
    if (!coverageRows) return [];
    return coverageRows.filter((c) => !!c.reasonNotSuggested);
  }, [coverageRows]);

  return (
    <div className="space-y-6 rounded-lg border bg-muted/30 p-4">
      <div>
        <h3 className="text-sm font-medium">All Searches</h3>
        <p className="text-xs text-muted-foreground">
          Shown below are snoozed or rejected searches and searches that haven't
          been recommended yet with details on why they are currently not
          recommended.
        </p>
      </div>

      <CandidateGroup
        title="Snoozed"
        emptyMessage="Nothing snoozed."
        rows={snoozedRows}
        statusLabel="Snoozed"
        onApprove={setConfirmRec}
      />

      <CandidateGroup
        title="Rejected"
        emptyMessage="Nothing rejected."
        rows={rejectedRows}
        statusLabel="Rejected"
      />

      <CandidateGroup
        title="Other available searches"
        emptyMessage="No other searches available."
        rows={supersededRows}
        statusLabel="Available"
        onApprove={setConfirmRec}
      />

      <div className="space-y-2">
        <h4 className="text-sm font-medium">
          Not yet suggested{" "}
          <span className="text-xs text-muted-foreground">
            ({notSuggested.length})
          </span>
        </h4>
        {coverageRows === null ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : notSuggested.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Every wired product has at least one record on this job — nothing
            unsuggested.
          </p>
        ) : (
          <ul className="divide-y rounded-md border bg-background">
            {notSuggested.map((c) => (
              <li
                key={c.productKey}
                className="flex flex-col gap-1 p-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
              >
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{c.displayName}</span>
                    <Badge
                      variant="outline"
                      className="text-[10px] font-normal uppercase tracking-wide"
                    >
                      {REGISTRY_LABEL[c.registry]}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {c.reasonNotSuggested}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

interface CandidateGroupProps {
  title: string;
  emptyMessage: string;
  rows: RecommendationRow[];
  statusLabel: string;
  onApprove?: (rec: RecommendationRow) => void;
}

function CandidateGroup({
  title,
  emptyMessage,
  rows,
  statusLabel,
  onApprove,
}: CandidateGroupProps) {
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium">
        {title}{" "}
        <span className="text-xs text-muted-foreground">({rows.length})</span>
      </h4>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyMessage}</p>
      ) : (
        <ul className="divide-y rounded-md border bg-background">
          {rows.map((rec) => (
            <li
              key={rec._id}
              className="flex flex-col gap-1 p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
            >
              <div className="space-y-1">
                <div className="text-sm font-medium">{rec.displayName}</div>
                {rec.sourceEntityLabel ? (
                  <div className="text-sm text-foreground/80">
                    {rec.sourceEntityLabel}
                  </div>
                ) : null}
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <ReasonBadge
                    reason={rec.reason}
                    generatedBy={rec.generatedBy}
                  />
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {onApprove ? (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => onApprove(rec)}
                  >
                    Approve
                  </Button>
                ) : (
                  <Badge variant="secondary" className="font-normal">
                    {statusLabel}
                  </Badge>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
